import { useState, type FormEvent } from 'react';
import { useCreateKfoReportMutation, type KfoReport, type KfoReportKind } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Input, Select } from '@shared/ui/Input';
import {
  Field,
  Muted,
  REPORT_KIND_LABEL,
  REPORT_KINDS,
  errorText,
  isRfiKind,
  todayIso,
} from './ui';

/**
 * Новый черновик отчёта: вид (Отчёт СМР / болтовые / Приложение к RFI
 * приёмочный или геодезический), дата отчёта, номер (необязательно).
 * Номер «Приложения к RFI» присваивает система при выпуске
 * (`<подрядчик>-<seq>-ACC|SUR`, docs/pto-kfo-spec.md §12) — поля для него нет.
 */
export function CreateReportForm({
  projectId,
  onCreated,
}: {
  projectId: string;
  onCreated: (r: KfoReport) => void;
}) {
  const [create, { isLoading }] = useCreateKfoReportMutation();
  const [kind, setKind] = useState<KfoReportKind>('smr');
  const [date, setDate] = useState(todayIso());
  const [number, setNumber] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!date) {
      setError('Укажите дату отчёта.');
      return;
    }
    try {
      const r = await create({
        projectId,
        kind,
        report_date: date,
        // Для RFI-видов сервер номер из запроса игнорирует — не шлём.
        number: isRfiKind(kind) ? undefined : number.trim() || undefined,
      }).unwrap();
      setNumber('');
      onCreated(r);
    } catch (err) {
      setError(errorText(err, 'Не удалось создать черновик.'));
    }
  }

  return (
    <form onSubmit={onSubmit}>
      <Field>
        Вид документа
        <Select value={kind} onChange={(e) => setKind(e.target.value as KfoReportKind)}>
          {REPORT_KINDS.map((k) => (
            <option key={k} value={k}>
              {REPORT_KIND_LABEL[k]}
            </option>
          ))}
        </Select>
      </Field>
      <Field>
        {isRfiKind(kind) ? 'Дата приложения' : 'Отчёт СМР за (дата)'}
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>
      {isRfiKind(kind) ? (
        <Field as="div">
          № приложения к RFI
          <Muted>номер присвоится при выпуске</Muted>
        </Field>
      ) : (
        <Field>
          Номер (необязательно)
          <Input value={number} onChange={(e) => setNumber(e.target.value)} />
        </Field>
      )}
      {isRfiKind(kind) && (
        <Muted style={{ marginBottom: 12 }}>
          {kind === 'rfi_acceptance'
            ? 'Приёмочный контроль: предъявлять можно не больше, чем Факт СМР накопительно по строке.'
            : 'Геодезический контроль: «Ранее принято по RFI» берётся из «Принято ГК».'}
        </Muted>
      )}
      <Button type="submit" disabled={isLoading}>
        {isLoading ? 'Создаю…' : 'Создать черновик'}
      </Button>
      {error && <Muted style={{ color: 'crimson', marginTop: 8 }}>{error}</Muted>}
    </form>
  );
}
